import {
  isGifBytes,
  KIND_AUDIO,
  KIND_FILE,
  KIND_GIF,
  KIND_IMAGE,
  KIND_STICKER,
  MAX_DOCUMENT_BYTES,
  MAX_GIF_BYTES,
  MAX_MEDIA_BYTES,
  sniffMime,
} from './zionMediaProtocol.ts'

export interface SanitizedMedia {
  bytes: Uint8Array
  mime: string
  kind: string
  fileName?: string
}

const MAX_INPUT_BYTES = 12 * 1024 * 1024

function looksLikeGif(raw: Uint8Array, mimeHint: string, fileName?: string): boolean {
  if (isGifBytes(raw)) return true
  const hint = mimeHint.trim().toLowerCase()
  if (hint === 'image/gif' || hint.includes('gif')) return true
  if (/\.gif$/i.test(fileName ?? '')) return true
  return false
}

function looksLikeWebp(raw: Uint8Array, mimeHint: string, fileName?: string): boolean {
  const sniffed = sniffMime(raw)
  if (sniffed === 'image/webp') return true
  const hint = mimeHint.trim().toLowerCase()
  if (hint === 'image/webp' || hint.includes('webp')) return true
  if (/\.webp$/i.test(fileName ?? '')) return true
  return false
}

function looksLikeHeic(raw: Uint8Array, mimeHint: string, fileName?: string): boolean {
  const hint = mimeHint.trim().toLowerCase()
  if (hint.includes('heic') || hint.includes('heif')) return true
  if (/\.heic$/i.test(fileName ?? '') || /\.heif$/i.test(fileName ?? '')) return true
  // ftyp....heic / heif / mif1
  if (
    raw.length >= 12 &&
    raw[4] === 0x66 &&
    raw[5] === 0x74 &&
    raw[6] === 0x79 &&
    raw[7] === 0x70
  ) {
    const brand = String.fromCharCode(raw[8]!, raw[9]!, raw[10]!, raw[11]!).toLowerCase()
    if (brand.includes('heic') || brand.includes('heif') || brand === 'mif1' || brand === 'msf1') {
      return true
    }
  }
  return false
}

/** Decode immagine via <img> (fallback iOS quando createImageBitmap fallisce, es. HEIC). */
async function decodeImageViaImg(raw: Uint8Array, mimeHint: string): Promise<HTMLImageElement | null> {
  const type = mimeHint.trim() || 'application/octet-stream'
  const url = URL.createObjectURL(new Blob([new Uint8Array(raw)], { type }))
  try {
    const img = new Image()
    img.decoding = 'async'
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('img decode failed'))
      img.src = url
    })
    if (!img.naturalWidth || !img.naturalHeight) return null
    return img
  } catch {
    return null
  } finally {
    URL.revokeObjectURL(url)
  }
}

async function rasterToJpeg(
  source: CanvasImageSource,
  width: number,
  height: number,
): Promise<SanitizedMedia | null> {
  let w = width
  let h = height
  const maxDim = 2048
  while (w > maxDim || h > maxDim) {
    w = Math.max(1, Math.floor(w * 0.75))
    h = Math.max(1, Math.floor(h * 0.75))
  }
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.drawImage(source, 0, 0, w, h)

  for (const q of [0.88, 0.75, 0.62, 0.5, 0.4]) {
    const outBlob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', q)
    })
    if (!outBlob) continue
    const buf = new Uint8Array(await outBlob.arrayBuffer())
    if (buf.length <= MAX_MEDIA_BYTES) {
      return { bytes: buf, mime: 'image/jpeg', kind: KIND_IMAGE }
    }
  }
  return null
}

/** Comprime immagini via canvas; pass-through GIF/WebP sticker; HEIC → JPEG su iOS. */
export async function sanitizeMediaFile(
  raw: Uint8Array,
  mimeHint: string,
  fileName?: string,
): Promise<SanitizedMedia | null> {
  if (!raw.length || raw.length > MAX_INPUT_BYTES) return null

  // GIF: mai passare da canvas (distrugge l'animazione).
  if (looksLikeGif(raw, mimeHint, fileName)) {
    if (raw.length > MAX_GIF_BYTES) return null
    return { bytes: raw, mime: 'image/gif', kind: KIND_GIF, fileName }
  }

  // Sticker WebP (anche animati): pass-through.
  if (looksLikeWebp(raw, mimeHint, fileName)) {
    if (raw.length > MAX_MEDIA_BYTES) return null
    return { bytes: raw, mime: 'image/webp', kind: KIND_STICKER, fileName }
  }

  const sniffed = sniffMime(raw)
  const hint = mimeHint.trim().toLowerCase()

  // HEIC/HEIF da Fotocamera iPhone → JPEG
  if (looksLikeHeic(raw, mimeHint, fileName)) {
    const converted = await compressImage(raw, hint || 'image/heic')
    if (converted) return { ...converted, fileName }
    return null
  }

  if (sniffed?.startsWith('image/') || hint.startsWith('image/')) {
    if (sniffed === 'image/gif' || hint === 'image/gif') {
      if (raw.length > MAX_GIF_BYTES) return null
      return { bytes: raw, mime: 'image/gif', kind: KIND_GIF, fileName }
    }
    // PNG: sticker piccoli pass-through; foto PNG grandi → JPEG compresso.
    if (sniffed === 'image/png' || hint === 'image/png' || /\.png$/i.test(fileName ?? '')) {
      const stickerLike =
        raw.length <= 512 * 1024 ||
        /sticker/i.test(fileName ?? '') ||
        /sticker/i.test(mimeHint)
      if (stickerLike && raw.length <= MAX_MEDIA_BYTES) {
        return { bytes: raw, mime: 'image/png', kind: KIND_STICKER, fileName }
      }
      if (raw.length <= 1024 * 1024 && raw.length <= MAX_MEDIA_BYTES) {
        return { bytes: raw, mime: 'image/png', kind: KIND_IMAGE, fileName }
      }
      const compressed = await compressImage(raw, 'image/png')
      return compressed ? { ...compressed, fileName } : null
    }
    const compressed = await compressImage(raw, hint || sniffed || 'image/jpeg')
    return compressed ? { ...compressed, fileName } : null
  }

  // iOS a volte dà type vuoto / application/octet-stream per GIF/sticker dalla Libreria.
  if (!hint || hint === 'application/octet-stream' || hint === '') {
    if (isGifBytes(raw)) {
      if (raw.length > MAX_GIF_BYTES) return null
      return { bytes: raw, mime: 'image/gif', kind: KIND_GIF, fileName }
    }
    if (sniffed?.startsWith('image/')) {
      return sanitizeMediaFile(raw, sniffed, fileName)
    }
    // Ultimo tentativo: prova a decodificare come immagine
    const decoded = await compressImage(raw, 'image/jpeg')
    if (decoded) return { ...decoded, fileName }
  }

  if (raw.length > MAX_DOCUMENT_BYTES) return null
  const mime = hint || sniffed || 'application/octet-stream'
  return { bytes: raw, mime, kind: KIND_FILE, fileName }
}

export function sanitizeAudioBytes(raw: Uint8Array, mimeHint: string): SanitizedMedia | null {
  if (!raw.length || raw.length < 100 || raw.length > MAX_MEDIA_BYTES) return null
  const sniffed = sniffMime(raw)
  const hint = mimeHint.trim().toLowerCase()

  const looksWav =
    raw.length >= 44 &&
    raw[0] === 0x52 &&
    raw[1] === 0x49 &&
    raw[2] === 0x46 &&
    raw[3] === 0x46 &&
    raw[8] === 0x57 &&
    raw[9] === 0x41 &&
    raw[10] === 0x56 &&
    raw[11] === 0x45
  if (looksWav || hint.includes('wav')) {
    return { bytes: raw, mime: 'audio/wav', kind: KIND_AUDIO }
  }

  if (hint.startsWith('audio/webm') || sniffed === 'audio/webm') {
    const mime = hint.includes('opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
    return { bytes: raw, mime, kind: KIND_AUDIO }
  }

  const resolved = hint.startsWith('audio/')
    ? hint.split(';')[0]!.trim()
    : sniffed?.startsWith('audio/')
      ? sniffed
      : null
  if (!resolved) return null
  return { bytes: raw, mime: resolved, kind: KIND_AUDIO }
}

async function compressImage(raw: Uint8Array, mimeHint: string): Promise<SanitizedMedia | null> {
  if (looksLikeGif(raw, mimeHint)) return null

  // Preferisci createImageBitmap; su iOS HEIC spesso fallisce → fallback <img>.
  try {
    const blob = new Blob([new Uint8Array(raw)], { type: mimeHint || 'application/octet-stream' })
    const bitmap = await createImageBitmap(blob)
    try {
      const out = await rasterToJpeg(bitmap, bitmap.width, bitmap.height)
      return out
    } finally {
      bitmap.close()
    }
  } catch {
    /* fallback sotto */
  }

  const img = await decodeImageViaImg(raw, mimeHint)
  if (!img) return null
  return rasterToJpeg(img, img.naturalWidth, img.naturalHeight)
}
